import { useCallback, useEffect, useState } from 'react';
import { Card, Stat, StatusBadge, Modal, FormField, TextInput, PageHeader } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';
import { RecentApiRequests } from './RecentApiRequests';

interface ApiData {
  base_url: string;
  auth_header?: string;
  endpoints?: { method: string; path: string; he: string }[];
  scopes?: { scope: string; he: string }[];
  rate_limits: { plan: string; name: string; limit: string }[];
  webhook_events: { event: string; he: string; live?: boolean }[];
  signature_headers: { header: string; he: string }[];
  stats: { api_keys_total: number; webhooks_total: number; webhooks_active: number };
}

interface TenantOption {
  id: number;
  name: string;
  slug: string;
}

interface ApiKeyRow {
  id: number;
  tenant_id: number;
  tenant_name: string;
  name: string;
  prefix: string;
  scopes: string[] | null;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string | null;
}

interface WebhookRow {
  id: number;
  tenant_id: number;
  tenant_name: string;
  url: string;
  events: string[];
  active: boolean;
  created_at: string | null;
}

const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString('he-IL') : '—');

export function SuperApi() {
  const [data, setData] = useState<ApiData | null>(null);
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [hooks, setHooks] = useState<WebhookRow[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(true);

  const [createKeyOpen, setCreateKeyOpen] = useState(false);
  const [createHookOpen, setCreateHookOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRow | null>(null);
  const [deleteHookTarget, setDeleteHookTarget] = useState<WebhookRow | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  // סינון "30 הבקשות האחרונות" לפי מפתח — נבחר מכפתור "בקשות" בשורת המפתח.
  const [logKey, setLogKey] = useState<ApiKeyRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [overview, k, w, t] = await Promise.all([
        superApi.platformApi(),
        superApi.platformApiKeys(),
        superApi.platformWebhooks(),
        superApi.tenants({ per_page: 500 }),
      ]);
      setData(overview as ApiData);
      setKeys(k as ApiKeyRow[]);
      setHooks(w as WebhookRow[]);
      setTenants(((t.data as TenantOption[]) ?? []).map((x) => ({ id: x.id, name: x.name, slug: x.slug })));
    } catch {
      toast('שגיאה בטעינת הגדרות API', 'danger');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function revokeKey(key: ApiKeyRow) {
    try {
      await superApi.revokeApiKey(key.id);
      toast('המפתח בוטל', 'ok');
      setRevokeTarget(null);
      setKeys((prev) => prev.filter((k) => k.id !== key.id));
    } catch {
      toast('שגיאה בביטול המפתח', 'danger');
    }
  }

  async function deleteHook(hook: WebhookRow) {
    try {
      await superApi.deleteWebhook(hook.id);
      toast('ה-webhook נמחק', 'ok');
      setDeleteHookTarget(null);
      setHooks((prev) => prev.filter((h) => h.id !== hook.id));
    } catch {
      toast('שגיאה במחיקת ה-webhook', 'danger');
    }
  }

  async function testHook(hook: WebhookRow) {
    setTestingId(hook.id);
    try {
      const res = await superApi.testWebhook(hook.id);
      if (res?.ok) {
        toast(`נשלחה בדיקה בהצלחה (HTTP ${res.status}, ${res.latency_ms}ms)`, 'ok');
      } else {
        toast(`הבדיקה נכשלה: ${res?.error ?? 'שגיאה'}`, 'danger');
      }
    } catch {
      toast('שגיאה בשליחת בדיקה', 'danger');
    } finally {
      setTestingId(null);
    }
  }

  async function toggleHook(hook: WebhookRow) {
    try {
      const res = await superApi.toggleWebhook(hook.id);
      const active = res?.active ?? !hook.active;
      setHooks((prev) => prev.map((h) => (h.id === hook.id ? { ...h, active } : h)));
      toast(active ? 'ה-webhook הופעל' : 'ה-webhook הושבת', 'ok');
    } catch {
      toast('שגיאה בעדכון ה-webhook', 'danger');
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 'var(--gap)' }}>
        <Card>טוען…</Card>
      </div>
    );
  }
  if (!data?.stats || !data?.rate_limits || !data?.signature_headers || !data?.webhook_events) {
    return (
      <div style={{ padding: 'var(--gap)' }}>
        <Card>שגיאה בטעינת הגדרות API.</Card>
      </div>
    );
  }

  const activeHooks = hooks.filter((h) => h.active).length;

  return (
    <div className="stack" style={{ padding: 'var(--gap)', gap: 'var(--gap)' }}>
      <div>
        <PageHeader title="API &amp; Webhooks" />
        <p style={{ color: 'var(--text-2)', fontSize: 'var(--t-sm)', margin: '6px 0 0' }}>
          ה-API הציבורי של הלקוחות זמין ב-<code dir="ltr">{data.base_url}</code>. כאן יוצרים ומנהלים מפתחות
          ו-webhooks עבור כל לקוח, וצופים במגבלות הקצב והאירועים הזמינים.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
        <Stat label="מפתחות API" value={data.stats.api_keys_total} />
        <Stat label="Webhooks רשומים" value={data.stats.webhooks_total} />
        <Stat label="Webhooks פעילים" value={activeHooks} />
      </div>

      {/* ===== API keys ===== */}
      <Card
        title={`מפתחות API (${keys.length})`}
        action={
          <button type="button" className="btn primary" onClick={() => setCreateKeyOpen(true)}>
            <Icon name="plus" size={14} /> מפתח חדש
          </button>
        }
      >
        {keys.length === 0 ? (
          <Empty>אין מפתחות API. צרו מפתח כדי לתת ללקוח גישה ל-REST API.</Empty>
        ) : (
          <DataGrid
            head={['לקוח', 'שם', 'Prefix', 'הרשאות', 'שימוש אחרון', 'תפוגה', '']}
            rows={keys.map((k) => {
              const expired = k.expires_at ? new Date(k.expires_at) < new Date() : false;
              return [
                k.tenant_name,
                k.name,
                <code key="p" dir="ltr" style={{ fontSize: 'var(--t-xs)' }}>{k.prefix}…</code>,
                <span key="s" style={{ fontSize: 'var(--t-xs)', color: 'var(--text-2)' }}>
                  {k.scopes && k.scopes.length ? k.scopes.join(', ') : 'גישה מלאה'}
                </span>,
                fmtDate(k.last_used_at),
                k.expires_at ? (
                  <span key="x" style={{ color: expired ? 'var(--danger)' : undefined }}>
                    {fmtDate(k.expires_at)}
                    {expired ? ' (פג)' : ''}
                  </span>
                ) : (
                  'ללא'
                ),
                <span key="a" style={{ display: 'inline-flex', gap: 6 }}>
                  <button
                    type="button"
                    className={'btn' + (logKey?.id === k.id ? ' active' : '')}
                    title="30 הבקשות האחרונות של המפתח הזה, כולל כשלים"
                    onClick={() => {
                      setLogKey(k);
                      document.getElementById('recent-api-requests')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    בקשות
                  </button>
                  <button type="button" className="btn danger" onClick={() => setRevokeTarget(k)}>
                    בטל
                  </button>
                </span>,
              ];
            })}
          />
        )}
      </Card>

      {/* ===== 30 בקשות אחרונות (כולל כשלים) ===== */}
      <div id="recent-api-requests">
        <RecentApiRequests
          keyId={logKey?.id ?? null}
          keyLabel={logKey ? `${logKey.tenant_name} · ${logKey.name}` : null}
          onClearKey={() => setLogKey(null)}
        />
      </div>

      {/* ===== Webhooks ===== */}
      <Card
        title={`Webhooks (${hooks.length})`}
        action={
          <button type="button" className="btn primary" onClick={() => setCreateHookOpen(true)}>
            <Icon name="plus" size={14} /> webhook חדש
          </button>
        }
      >
        {hooks.length === 0 ? (
          <Empty>אין webhooks. צרו webhook כדי לדחוף אירועי שיחה ל-URL של לקוח.</Empty>
        ) : (
          <DataGrid
            head={['לקוח', 'URL', 'אירועים', 'סטטוס', '']}
            rows={hooks.map((h) => [
              h.tenant_name,
              <code key="u" dir="ltr" style={{ fontSize: 'var(--t-xs)', wordBreak: 'break-all' }}>{h.url}</code>,
              <span key="e" style={{ fontSize: 'var(--t-xs)', color: 'var(--text-2)' }}>{h.events.join(', ')}</span>,
              <StatusBadge key="s" tone={h.active ? 'ok' : 'neutral'} dot size="sm">
                {h.active ? 'פעיל' : 'מושבת'}
              </StatusBadge>,
              <div key="a" style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button type="button" className="btn ghost" disabled={testingId === h.id} onClick={() => testHook(h)}>
                  {testingId === h.id ? 'בודק…' : 'בדיקה'}
                </button>
                <button type="button" className="btn ghost" onClick={() => toggleHook(h)}>
                  {h.active ? 'השבת' : 'הפעל'}
                </button>
                <button type="button" className="btn danger" onClick={() => setDeleteHookTarget(h)}>
                  מחק
                </button>
              </div>,
            ])}
          />
        )}
      </Card>

      {/* ===== Reference ===== */}
      <Card title="התייחסות — REST API">
        <p style={{ margin: '0 0 12px', fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
          אימות: <code dir="ltr">{data.auth_header ?? 'X-Api-Key: <key>'}</code>
        </p>
        {data.endpoints && data.endpoints.length > 0 && (
          <DataGrid
            head={['Method', 'Path', 'תיאור']}
            rows={data.endpoints.map((e) => [
              <StatusBadge key="m" tone="neutral" size="sm">{e.method}</StatusBadge>,
              <code key="p" dir="ltr" style={{ fontSize: 'var(--t-xs)' }}>{e.path}</code>,
              e.he,
            ])}
          />
        )}
        {data.scopes && data.scopes.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <strong style={{ fontSize: 'var(--t-sm)' }}>Scopes:</strong>
            <ul style={{ margin: '8px 0 0', paddingInlineStart: 16, fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
              {data.scopes.map((s) => (
                <li key={s.scope}>
                  <code dir="ltr">{s.scope}</code> — {s.he}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--gap)' }}>
        <Card title="מגבלות קצב (לפי חבילה)">
          <DataGrid
            head={['חבילה', 'מגבלה']}
            rows={data.rate_limits.map((r) => [r.name, r.limit])}
          />
        </Card>

        <Card title="חתימת Webhooks">
          <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
            כל webhook נשלח עם כותרות אימות:
          </p>
          <ul style={{ margin: '8px 0 0', paddingInlineStart: 16, fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
            {data.signature_headers.map((h) => (
              <li key={h.header}>
                <code dir="ltr">{h.header}</code> — {h.he}
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card title="אירועי Webhook">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
          {data.webhook_events.map((e) => (
            <div key={e.event} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)' }}>
              <StatusBadge tone={e.live ? 'ok' : 'neutral'} size="sm" dot>
                {e.live ? 'פעיל' : 'בקרוב'}
              </StatusBadge>
              <code dir="ltr">{e.event}</code>
              <span style={{ color: 'var(--text-2)' }}>— {e.he}</span>
            </div>
          ))}
        </div>
      </Card>

      {createKeyOpen && (
        <CreateKeyModal
          tenants={tenants}
          scopes={data.scopes ?? []}
          onClose={() => setCreateKeyOpen(false)}
          onCreated={() => {
            setCreateKeyOpen(false);
            void load();
          }}
        />
      )}

      {createHookOpen && (
        <CreateWebhookModal
          tenants={tenants}
          events={data.webhook_events}
          onClose={() => setCreateHookOpen(false)}
          onCreated={() => {
            setCreateHookOpen(false);
            void load();
          }}
        />
      )}

      {revokeTarget && (
        <Modal
          isOpen
          onClose={() => setRevokeTarget(null)}
          title="ביטול מפתח API"
          footer={
            <>
              <button type="button" className="btn" onClick={() => setRevokeTarget(null)}>ביטול</button>
              <button type="button" className="btn danger" onClick={() => revokeKey(revokeTarget)}>בטל מפתח</button>
            </>
          }
        >
          <p>
            לבטל את המפתח <strong>{revokeTarget.name}</strong> (<code dir="ltr">{revokeTarget.prefix}…</code>) של{' '}
            <strong>{revokeTarget.tenant_name}</strong>? כל בקשה עם המפתח תיכשל מיידית.
          </p>
        </Modal>
      )}

      {deleteHookTarget && (
        <Modal
          isOpen
          onClose={() => setDeleteHookTarget(null)}
          title="מחיקת Webhook"
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDeleteHookTarget(null)}>ביטול</button>
              <button type="button" className="btn danger" onClick={() => deleteHook(deleteHookTarget)}>מחק</button>
            </>
          }
        >
          <p>
            למחוק את ה-webhook של <strong>{deleteHookTarget.tenant_name}</strong> (
            <code dir="ltr">{deleteHookTarget.url}</code>)? לא יישלחו אליו עוד אירועים.
          </p>
        </Modal>
      )}
    </div>
  );
}

/* ---------- Create API key ---------- */
function CreateKeyModal({
  tenants,
  scopes,
  onClose,
  onCreated,
}: {
  tenants: TenantOption[];
  scopes: { scope: string; he: string }[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [tenantId, setTenantId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  // Real scopes only (exclude the '*' catch-all from the picker — leaving all
  // unticked already means full access).
  const pickable = scopes.filter((s) => s.scope !== '*');

  function toggleScope(scope: string) {
    setSelectedScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  }

  async function save() {
    if (!tenantId || !name.trim()) {
      toast('יש לבחור לקוח ולמלא שם', 'danger');
      return;
    }
    setSaving(true);
    try {
      const res = await superApi.createApiKey({
        tenant_id: Number(tenantId),
        name: name.trim(),
        scopes: selectedScopes.length ? selectedScopes : null,
        expires_at: expiresAt || null,
      });
      setCreatedKey(res.key);
      toast('המפתח נוצר', 'ok');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה ביצירת המפתח';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  // After creation, show the key ONCE.
  if (createdKey) {
    return (
      <Modal
        isOpen
        onClose={onCreated}
        title="המפתח נוצר"
        footer={<button type="button" className="btn primary" onClick={onCreated}>סיום</button>}
      >
        <div className="stack" style={{ gap: 10 }}>
          <p style={{ margin: 0, color: 'var(--danger)', fontSize: 'var(--t-sm)' }}>
            ⚠️ זו הפעם היחידה שהמפתח מוצג. העתיקו ושמרו אותו — לא ניתן לשחזר.
          </p>
          <div
            dir="ltr"
            style={{
              padding: 'var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)',
              fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)', wordBreak: 'break-all',
            }}
          >
            {createdKey}
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => {
              void navigator.clipboard?.writeText(createdKey);
              toast('הועתק', 'ok');
            }}
          >
            <Icon name="copy" size={14} /> העתק
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="מפתח API חדש"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'יוצר…' : 'צור מפתח'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="לקוח" required>
          <select className="select" value={tenantId} onChange={(e) => setTenantId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">בחר לקוח…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="שם המפתח" required hint="לזיהוי בלבד, למשל: אינטגרציית CRM">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </FormField>
        <FormField label="הרשאות (scopes)" hint="ללא בחירה = גישה מלאה לכל ה-endpoints">
          <div style={{ display: 'grid', gap: 6 }}>
            {pickable.map((s) => (
              <label key={s.scope} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)' }}>
                <input type="checkbox" checked={selectedScopes.includes(s.scope)} onChange={() => toggleScope(s.scope)} />
                <code dir="ltr">{s.scope}</code>
                <span style={{ color: 'var(--text-2)' }}>— {s.he}</span>
              </label>
            ))}
          </div>
        </FormField>
        <FormField label="תפוגה" hint="אופציונלי — השאר ריק למפתח ללא תפוגה">
          <TextInput type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} dir="ltr" />
        </FormField>
      </div>
    </Modal>
  );
}

/* ---------- Create webhook ---------- */
function CreateWebhookModal({
  tenants,
  events,
  onClose,
  onCreated,
}: {
  tenants: TenantOption[];
  events: { event: string; he: string; live?: boolean }[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [tenantId, setTenantId] = useState<number | ''>('');
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);

  function toggleEvent(ev: string) {
    setSelectedEvents((prev) => (prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev]));
  }

  async function save() {
    if (!tenantId || !url.trim() || selectedEvents.length === 0) {
      toast('יש לבחור לקוח, URL ולפחות אירוע אחד', 'danger');
      return;
    }
    setSaving(true);
    try {
      const res = await superApi.createWebhook({
        tenant_id: Number(tenantId),
        url: url.trim(),
        events: selectedEvents,
      });
      setSecret(res.signing_secret);
      toast('ה-webhook נוצר', 'ok');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה ביצירת ה-webhook';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  if (secret) {
    return (
      <Modal
        isOpen
        onClose={onCreated}
        title="ה-webhook נוצר"
        footer={<button type="button" className="btn primary" onClick={onCreated}>סיום</button>}
      >
        <div className="stack" style={{ gap: 10 }}>
          <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
            ה-signing secret לאימות חתימת ה-HMAC (העתיקו אותו לצד הלקוח):
          </p>
          <div
            dir="ltr"
            style={{
              padding: 'var(--pad-card)', background: 'var(--surface-2)', borderRadius: 'var(--r-2)',
              fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)', wordBreak: 'break-all',
            }}
          >
            {secret}
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => {
              void navigator.clipboard?.writeText(secret);
              toast('הועתק', 'ok');
            }}
          >
            <Icon name="copy" size={14} /> העתק
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Webhook חדש"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'יוצר…' : 'צור webhook'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="לקוח" required>
          <select className="select" value={tenantId} onChange={(e) => setTenantId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">בחר לקוח…</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="URL" required>
          <TextInput value={url} onChange={(e) => setUrl(e.target.value)} dir="ltr" placeholder="https://example.com/webhook" />
        </FormField>
        <FormField label="אירועים" required hint="אירועים מסומנים 'בקרוב' עדיין לא נשלחים">
          <div style={{ display: 'grid', gap: 6 }}>
            {events.map((ev) => (
              <label key={ev.event} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-sm)' }}>
                <input type="checkbox" checked={selectedEvents.includes(ev.event)} onChange={() => toggleEvent(ev.event)} />
                <code dir="ltr">{ev.event}</code>
                <span style={{ color: 'var(--text-2)' }}>— {ev.he}</span>
                {!ev.live && <StatusBadge tone="neutral" size="sm">בקרוב</StatusBadge>}
              </label>
            ))}
          </div>
        </FormField>
      </div>
    </Modal>
  );
}

/* ---------- shared bits ---------- */
function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', padding: '8px 0' }}>{children}</div>;
}

function DataGrid({ head, rows }: { head: React.ReactNode[]; rows: React.ReactNode[][] }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            {head.map((h, i) => (
              <th key={i} style={{ padding: '8px 12px', textAlign: 'start', fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 600 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: '1px solid var(--line)' }}>
              {r.map((c, j) => (
                <td key={j} style={{ padding: '8px 12px', fontSize: 'var(--t-sm)', verticalAlign: 'middle' }}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
